import { useState, useEffect } from 'react';
import { FileText, Eye } from 'lucide-react';
import { getSubmissionsForLead } from '../../services/formService';
import { SubmissionAnswersModal } from './SubmissionAnswersModal';
import type { FormSubmissionWithAnswers } from '../../types/forms';
import { logger } from '../../lib/logger';

interface LeadSubmissionsProps {
  leadId: string;
  userId: string;
}

export function LeadSubmissions({ leadId, userId }: LeadSubmissionsProps) {
  const [submissions, setSubmissions] = useState<FormSubmissionWithAnswers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmissionWithAnswers | null>(null);

  useEffect(() => {
    loadSubmissions();
  }, [leadId, userId]);

  const loadSubmissions = async () => {
    try {
      const data = await getSubmissionsForLead(leadId, userId);
      setSubmissions(data);
    } catch (error) {
      logger.error('Error loading submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <FileText className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-gray-500 text-sm">
          Keine Formulareinreichungen vorhanden
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {submissions.map((submission) => (
          <div
            key={submission.id}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">
                  {submission.form?.name || 'Formular'}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(submission.submitted_at)}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedSubmission(submission)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              Antworten
            </button>
          </div>
        ))}
      </div>

      {selectedSubmission && (
        <SubmissionAnswersModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </>
  );
}
